import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Observable, forkJoin, fromEvent, of, retry } from 'rxjs';
import { Store } from '@ngrx/store';
import { catchError, take } from 'rxjs/operators';

// Redux
import { RootState } from '../../../reducers';

// Models
import { CommonIdPlaylist } from '../../../models/common-id/common-id-playlist';
import { Playlist, ThemeForDisplay } from '../../../models/playlist';
import {
  ReadableEnglishPlaylistProblem,
  ReadableHistoryPlaylistProblem,
  ReadableNationalLanguagePlaylistProblem,
  ReadableProblem,
  ReadableSciencePlaylistProblem
} from '../../../models/problem';
import { User } from '../../../models/user';
import { University } from '../../../models/common-data';
import { StaticCommonData } from 'src/app/models/static-common-data';

// Utils
import { Dates } from '../../../utils/dates';
import { Log } from '../../../utils/log';
import { ProblemImageUtil } from '../../../utils/problem-image-util';
import { splitImageItemsByMixedLayout } from '../../../utils/mixed-layout-util';
import { CommonIdSubjectUtil } from 'src/app/utils/common-id/common-id-subject-util';

// Others
import { environment } from '../../../../environments/environment';

interface ProblemImage {
  url: string;
  width: number;
  height: number;
}

interface ImageItem {
  readableProblemId: string;
  fImage?: ProblemImage;
  problemImages?: ProblemImage[];
  layoutType?: number;
  headerLabel?: string;
}

@Component({
  selector: 'app-playlist-problems-for-print',
  templateUrl: './playlist-problems-for-print.component.html',
  styleUrls: ['./playlist-problems-for-print.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlaylistProblemsForPrintComponent implements OnChanges {
  @Input() menuType: 'article' | 'spellcheck' = 'article';
  @Input() universities: University[];
  @Input() staticCommonData: StaticCommonData;
  @Input() readablePlaylistProblemsByThemeId:
    | ThemeForDisplay<ReadableEnglishPlaylistProblem>
    | ThemeForDisplay<ReadableSciencePlaylistProblem>
    | ThemeForDisplay<ReadableNationalLanguagePlaylistProblem>
    | ThemeForDisplay<ReadableHistoryPlaylistProblem>;
  @Input() playlist: Playlist | CommonIdPlaylist;
  @Input() paperMode: boolean;
  @Input() trialMode: boolean;
  @Input() subjectId: string;
  // 頭紙フッタ部分のテキストをBtoS、BtoCで切り替えるためのフラグ
  @Input() isBToC: boolean;
  // BtoSの場合のみ必要
  @Input() signedInUser: User;

  // お気に入りリストの印刷で使用する変数
  @Input() isFromBookmark = false; // お気に入りリストの印刷かどうかを判断するフラグのため不要のはず

  @Output() dataReadyForPrint = new EventEmitter<void>();

  playlistTheme:
    | ThemeForDisplay<ReadableEnglishPlaylistProblem>
    | ThemeForDisplay<ReadableSciencePlaylistProblem>
    | ThemeForDisplay<ReadableNationalLanguagePlaylistProblem>
    | ThemeForDisplay<ReadableHistoryPlaylistProblem>;
  problemId: string;
  year: string;
  layoutType: number;
  subjectName: string;
  examDate: string;
  nyushiName: string;
  examTime: string;
  specialInfoData: string[];
  fImage: ProblemImage;
  problemImages: ProblemImage[];
  daimonId: string | null;
  daimonName: string | null;
  seriesName: string;
  headerCoverLabel: string;
  headerProblemLabel: string;
  originalSources: string[];
  outputDate: string;
  menuLabel: string;

  // 表紙以外の印刷で使用する変数
  imageItemsMap: { [problemId: string]: ImageItem[] } = {};
  imageItemsMap$: Observable<{ [problemId: string]: ImageItem[] }> = of({});
  // 表紙の印刷で使用する変数
  printData$: Observable<
    | ThemeForDisplay<ReadableEnglishPlaylistProblem>
    | ThemeForDisplay<ReadableSciencePlaylistProblem>
    | ThemeForDisplay<ReadableNationalLanguagePlaylistProblem>
    | ThemeForDisplay<ReadableHistoryPlaylistProblem>
  >;

  private LOG_SOURCE = this.constructor.name;

  constructor(private store: Store<RootState>, private detector: ChangeDetectorRef) {}

  async ngOnChanges(): Promise<void> {
    this.imageItemsMap = {};
    this.setUpDataForCoverPage();

    if (this.playlist) {
      // BtoCのおすすめ問題詳細ではシリーズ名が入る
      this.seriesName = (this.playlist as CommonIdPlaylist).seriesName;
    }

    // 全問題に対して非同期処理
    const tasks = this.readablePlaylistProblemsByThemeId.problems.map(problem => {
      if (problem.questionLayout !== undefined && problem.answerLayout !== undefined) {
        return this.setUpDataForEachProblemAsync(problem);
      }
    });
    await Promise.all(tasks);

    // レイアウト混在 paper のみ daimon 単位に分割し、画像名 _l\d から layoutType を割り当てる（混在無しは noop）
    // split により items が daimon 単位になり .print-page (page-break-after: always) で大問改ページが自動的に効く
    Object.keys(this.imageItemsMap).forEach(problemId => {
      this.imageItemsMap[problemId] = splitImageItemsByMixedLayout(this.imageItemsMap[problemId], this.menuType);
    });

    this.imageItemsMap$ = of(this.imageItemsMap); // 表紙以外の印刷データ
    this.printData$ = of(this.readablePlaylistProblemsByThemeId); // 表紙の印刷データ
    this.dataReadyForPrint.emit();

    this.detector.detectChanges();
  }

  calcImageLayout1(layoutType: number, images: ProblemImage[]): Array<Array<ProblemImage[]>> {
    const layoutImages: Array<Array<ProblemImage[]>> = [];
    let pages: Array<ProblemImage[]> = [];
    let containers: ProblemImage[] = [];

    if (images === undefined) {
      return layoutImages;
    }

    const maxHeight = Number(
      String(getComputedStyle(document.querySelector('.playlist-problems-for-print')).getPropertyValue('--pd-print-content-h'))
        .trim()
        .replace('px', '')
    );
    const maxWidth = Number(
      String(getComputedStyle(document.querySelector('.playlist-problems-for-print')).getPropertyValue('--pd-print-type-1-max-w'))
        .trim()
        .replace('px', '')
    );
    const gapY = Number(
      String(getComputedStyle(document.querySelector('.playlist-problems-for-print')).getPropertyValue('--pd-print-type-1-gap-y'))
        .trim()
        .replace('px', '')
    );

    let height = 0;
    let daimon = '';
    images.map((image, index) => {
      const currentDaimon: string = this.getDaimonId(image.url);
      if (daimon === '') daimon = currentDaimon;

      // aspect ratio
      if (image.width > maxWidth) {
        image.height = image.height * (maxWidth / image.width);
        image.width = maxWidth;
      }

      // aspect ratio
      if (image.height > maxHeight) {
        image.width = image.width * (maxHeight / image.height);
        image.height = maxHeight;
      }

      if (height + image.height > maxHeight) {
        pages.push(containers);
        layoutImages.push(pages);
        containers = [];
        pages = [];
        height = 0;
      } else if (daimon !== currentDaimon) {
        daimon = currentDaimon;

        pages.push(containers);
        containers = [];
        height += gapY;
      }

      height += image.height;
      containers.push(image);

      if (index === images.length - 1) {
        if (containers.length > 0) pages.push(containers);
        if (pages.length > 0) layoutImages.push(pages);
      }
    });

    Log.debug(this.LOG_SOURCE, `calcImageLayout1:`, layoutImages);
    return layoutImages;
  }

  calcImageLayout2(layoutType: number, images: ProblemImage[]): Array<Array<ProblemImage[]>> {
    const layoutImages: Array<Array<ProblemImage[]>> = [];
    let pages: Array<ProblemImage[]> = [];
    let containers: ProblemImage[] = [];

    if (images === undefined) {
      return layoutImages;
    }

    const maxWidth = Number(
      String(getComputedStyle(document.querySelector('.playlist-problems-for-print')).getPropertyValue('--pd-print-type-2-max-w'))
        .trim()
        .replace('px', '')
    );
    const maxHeight = Number(
      String(getComputedStyle(document.querySelector('.playlist-problems-for-print')).getPropertyValue('--pd-print-type-2-max-h'))
        .trim()
        .replace('px', '')
    );
    const offSetTop = Number(
      String(getComputedStyle(document.querySelector('.playlist-problems-for-print')).getPropertyValue('--pd-print-type-2-offset-top'))
        .trim()
        .replace('px', '')
    );
    const gapX = Number(
      String(getComputedStyle(document.querySelector('.playlist-problems-for-print')).getPropertyValue('--pd-print-type-2-gap-x'))
        .trim()
        .replace('px', '')
    );
    const gapY = Number(
      String(getComputedStyle(document.querySelector('.playlist-problems-for-print')).getPropertyValue('--pd-print-type-2-gap-y'))
        .trim()
        .replace('px', '')
    );

    let width = 0;
    let daimon = '';
    images.map((image, index) => {
      const currentDaimon: string = this.getDaimonId(image.url);
      if (daimon === '') daimon = currentDaimon;

      // aspect ratio
      if (image.height > maxHeight) {
        image.width = image.width * (maxHeight / image.height);
        image.height = maxHeight;
      }

      // aspect ratio
      if (image.width > maxWidth) {
        image.height = image.height * (maxWidth / image.width);
        image.width = maxWidth;
      }

      if (width + image.width > maxWidth) {
        pages.push(containers);
        if (pages.length === 2) {
          layoutImages.push(pages);
          pages = [];
        }
        containers = [];
        width = 0;
      } else if (daimon !== currentDaimon) {
        daimon = currentDaimon;

        pages.push(containers);
        if (pages.length === 2) {
          layoutImages.push(pages);
          pages = [];
        }
        containers = [];
        width = 0;
      }

      width += image.width + gapX;
      containers.push(image);

      if (index === images.length - 1) {
        if (containers.length > 0) pages.push(containers);
        if (pages.length > 0) layoutImages.push(pages);
      }
    });

    Log.debug(this.LOG_SOURCE, `calcImageLayout2:`, layoutImages);
    return layoutImages;
  }

  calcImageLayout3(layoutType: number, images: ProblemImage[]): Array<Array<ProblemImage[]>> {
    const layoutImages: Array<Array<ProblemImage[]>> = [];
    let pages: Array<ProblemImage[]> = [];
    let containers: ProblemImage[] = [];

    if (images === undefined) {
      return layoutImages;
    }

    images.map((image, index) => {
      containers.push(image);

      if (containers.length === 2) {
        pages.push(containers);
        containers = [];

        if (pages.length === 2) {
          layoutImages.push(pages);
          pages = [];
        }
      }

      if (index === images.length - 1) {
        if (containers.length > 0) pages.push(containers);
        if (pages.length > 0) layoutImages.push(pages);
      }
    });

    Log.debug(this.LOG_SOURCE, `calcImageLayout3:`, layoutImages);
    return layoutImages;
  }

  private getDaimonId(url: string): string {
    // 掲載見合わせ画像の場合、大問IDがないので、ない場合には乱数で返す
    const daimonId: string | undefined = url.match('.+/(.+?).[a-z]+([?#;].*)?$')[1].split('_')[1];
    return daimonId
      ? daimonId
      : Math.random()
          .toString(32)
          .substring(2);
  }

  private getImage(imageUrl: string): Observable<ImageData | Error> {
    return new Observable<any>(observer => {
      const image = new Image();
      fromEvent(image, 'load')
        .pipe(take(1))
        .subscribe(e => {
          Log.debug(this.LOG_SOURCE, `load success:`, e.target);

          observer.next(e.target);
          observer.complete();
        });
      fromEvent(image, 'error')
        .pipe(take(1))
        .subscribe(error => {
          Log.debug(this.LOG_SOURCE, `load error:`, error);

          observer.error(error);
          image.remove();
        });
      image.src = imageUrl;
    });
  }

  private setUpDataForCoverPage() {
    this.menuLabel = this.menuType === 'article' ? '問題' : '研究・解答';
    this.outputDate = Dates.simple4YmdStringFromIso(Dates.now());
  }

  private async setUpDataForEachProblemAsync(readableProblem: ReadableProblem): Promise<void> {
    this.problemId = readableProblem.id;

    // problemId ごとの配列を初期化
    if (!this.imageItemsMap[this.problemId]) {
      this.imageItemsMap[this.problemId] = [];
    }

    this.year = `20${this.problemId.slice(0, 2)}`;
    this.layoutType = this.menuType === 'article' ? readableProblem.questionLayout.layoutType : readableProblem.answerLayout.layoutType;

    this.daimonId = this.problemId.slice(-2);
    this.daimonName = this.problemId.slice(-1);

    // const examPaper = readableProblem.examPaper;

    const paperId = this.problemId.slice(0, -2);
    const headerSubjectName = CommonIdSubjectUtil.getSubjectName(paperId, this.staticCommonData.subjects);

    if (readableProblem.isOriginalProblem) {
      this.originalSources = readableProblem.originalSource.match(/^[0-9]+:/)
        ? readableProblem.originalSource.split(':').map(description => {
            const year = description.slice(0, 2);
            const universityId = description.slice(2, 6);
            const universityName = this.universities.find(univ => univ.id === `D${universityId}`)?.name ?? '';
            let problemNumber = description.slice(10, 12);
            if (problemNumber.startsWith('0')) {
              problemNumber = problemNumber.slice(1);
            }
            return `20${year}年度 ${universityName} 大問${problemNumber}`;
          })
        : [readableProblem.originalSource];
      this.subjectName = headerSubjectName; // 表紙のタイトル部分の科目表示
      this.headerCoverLabel = `${headerSubjectName} ${readableProblem.originalOverview} ${this.menuLabel}`;
      this.headerProblemLabel = `〔${headerSubjectName} 20${paperId.slice(0, 2)}年度 ${readableProblem.originalOverview}〕`;
    } else {
      this.subjectName = headerSubjectName; // 表紙のタイトル部分の科目表示
      this.headerCoverLabel = `${this.year}年 ${headerSubjectName} ${readableProblem?.university} ${this.menuLabel}`;
      this.headerProblemLabel = `〔${this.year}年 ${headerSubjectName} ${readableProblem?.university}〕`;
    }

    const problem = this.readablePlaylistProblemsByThemeId.problems.find(p => p.id === readableProblem.id);
    if (problem) {
      problem.headerLabel = this.headerCoverLabel;
      problem.coverLabelYear = this.year;
      problem.daimonName = this.daimonName;
      problem.subjectName = headerSubjectName;
    }

    // 該当の問題情報を登録
    const item: ImageItem = { readableProblemId: this.problemId, layoutType: this.layoutType, headerLabel: this.headerProblemLabel };
    this.imageItemsMap[this.problemId].push(item);

    await this.setUpProblemImagesAsync(readableProblem);
  }

  private async setUpProblemImagesAsync(readableProblem: ReadableProblem): Promise<void> {
    return new Promise<void>(resolve => {
      const problemId = readableProblem.id;

      // 問題IDごとのimageItemsが存在しない場合は初期化
      if (!this.imageItemsMap[problemId]) {
        this.imageItemsMap[problemId] = [];
      }

      const targetImages = this.menuType === 'article' ? readableProblem.questionImages : readableProblem.answerImages;
      if (!targetImages || targetImages.length === 0) {
        resolve();
        return;
      }

      const imageUrls = targetImages.map(img => environment.cloudfrontHost + ProblemImageUtil.getPath(img, this.trialMode));
      const filteredUrls = this.isFromBookmark
        ? imageUrls
        : imageUrls.filter(url => this.daimonId === null || url.includes((this.menuType === 'article' ? 'q' : 'a') + this.daimonId));
      const requests$ = filteredUrls.map(url =>
        this.getImage(url).pipe(
          retry(3),
          take(1),
          catchError(() => of(null))
        )
      );
      forkJoin(requests$).subscribe(images => {
        this.problemImages = images
          .map((image, i) => {
            if (!image || image instanceof Error) return null;
            const isRetina = filteredUrls[i].includes('@2x');
            return {
              width: isRetina ? image.width / 2 : image.width,
              height: isRetina ? image.height / 2 : image.height,
              url: filteredUrls[i]
            };
          })
          .filter(Boolean) as ProblemImage[];

        // 該当の問題IDの画像情報を登録
        const item = this.imageItemsMap[problemId][0];
        if (item) {
          item.problemImages = this.problemImages;
        }

        resolve();
      });
    });
  }
}
